/**
 * Append a Google Fonts stylesheet once, from the component that needs it,
 * rather than from each app's index.html — which would fetch the families on
 * every route for the benefit of a few.
 *
 * Idempotent by `id`: React 18 StrictMode runs effects twice in development,
 * and several routes can share one family set within a session. Callers pass a
 * `display=swap` URL, so the page paints immediately in the fallback stack and
 * swaps — nothing ever sits invisible waiting on a webfont.
 */
export function ensureGoogleFonts(id: string, href: string): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(id)) return;

  // The preconnects are shared by every family set; add them only once.
  if (!document.querySelector('link[rel="preconnect"][href="https://fonts.gstatic.com"]')) {
    const preconnect = (url: string, crossOrigin?: boolean) => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = url;
      if (crossOrigin) link.crossOrigin = '';
      document.head.appendChild(link);
    };
    preconnect('https://fonts.googleapis.com');
    preconnect('https://fonts.gstatic.com', true);
  }

  const sheet = document.createElement('link');
  sheet.id = id;
  sheet.rel = 'stylesheet';
  sheet.href = href;
  document.head.appendChild(sheet);
}
